import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';

export type PunchCardStatus = 'active' | 'used' | 'expired' | 'cancelled';

export interface PortalPunchCardItem {
  id: string;
  clinic_id: string;
  name: string;
  total_clips: number;
  used_clips: number;
  status: PunchCardStatus;
  purchased_at: string;
  expires_at: string | null;
  punch_card_number: string | null;
}

export const portalPunchCardsQueryKey = (patientId: string | null) =>
  ['portal', 'punch-cards', patientId] as const;

export function usePortalPunchCards() {
  const { activePatientId, isLoading: clinicsLoading } = usePortalActiveClinic();

  const query = useQuery({
    queryKey: portalPunchCardsQueryKey(activePatientId),
    enabled: Boolean(activePatientId),
    queryFn: async (): Promise<PortalPunchCardItem[]> => {
      if (!activePatientId) return [];

      const { data, error } = await supabase
        .from('punch_cards')
        .select('id, clinic_id, name, total_clips, used_clips, status, purchased_at, expires_at, punch_card_number')
        .eq('patient_id', activePatientId)
        .order('purchased_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as PortalPunchCardItem[];
    },
    staleTime: 30_000,
  });

  return {
    ...query,
    isLoading: clinicsLoading || query.isLoading,
  };
}
